import type { BudgetPeriod, CommitmentType, CostpointType } from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getCostpointLabel } from '../utils/costpointLabels';

interface AddCommitmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: NewCommitmentData) => Promise<void>;
  // Every budget period for the selected year (ANNUAL/H1/H2, whichever exist) - the
  // candidates a commitment can be attached to. Empty when no budget period exists yet for
  // the year, in which case the form stays disabled with an explanatory message.
  availablePeriods: BudgetPeriod[];
  // Pre-selected default among availablePeriods, based on whatever the Forecast page is
  // currently viewing - the user can still change it via the picker below.
  defaultBudgetPeriodId: number | null;
  year: number; // shown in the disabled-state message when availablePeriods is empty
}

export interface NewCommitmentData {
  budget_period_id: number;
  costpoint: CostpointType;
  type: CommitmentType;
  amount: number;
  description: string;
}

// BudgetPeriod.start_date/end_date are DATE columns, but the mariadb driver returns DATE
// values as real JS Date objects (no `dateStrings` option set on the pool - see
// DatabaseService), which JSON-serializes to a full ISO timestamp like
// "2026-01-01T00:00:00.000Z", not a plain "YYYY-MM-DD". Slicing to the first 10 chars
// tolerates either shape before re-parsing as a local-midnight date, so this doesn't break
// if a query elsewhere ever does return a plain date string instead.
function formatDateShort(isoDate: string): string {
  const datePart = isoDate.slice(0, 10);
  return new Date(`${datePart}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatPeriodOption(period: BudgetPeriod): string {
  return `${period.period_type} (${formatDateShort(period.start_date)} – ${formatDateShort(period.end_date)})`;
}

// Mirrors BulkEditModal.tsx's costpointOptions - no shared costpoint selector component
// exists yet in this codebase (see the frontend research notes), so this copies that
// established inline pattern rather than introducing a new abstraction for one caller.
const costpointOptions: { value: CostpointType; label: string }[] = [
  { value: 'cs', label: 'CS' },
  { value: 'pubg', label: 'PUBG' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' },
];

const AddCommitmentModal: React.FC<AddCommitmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  availablePeriods,
  defaultBudgetPeriodId,
  year,
}) => {
  const [costpoint, setCostpoint] = useState<CostpointType>('cs');
  const [type, setType] = useState<CommitmentType>('expenses');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [budgetPeriodId, setBudgetPeriodId] = useState<number | null>(defaultBudgetPeriodId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state whenever the modal opens, matching BulkEditModal.tsx's convention.
  // The period picker re-seeds from whatever the page currently suggests, but stays a
  // separate, visible, changeable field rather than an invisible decision.
  useEffect(() => {
    if (isOpen) {
      setCostpoint('cs');
      setType('expenses');
      setAmount('');
      setDescription('');
      setBudgetPeriodId(defaultBudgetPeriodId ?? availablePeriods[0]?.id ?? null);
      setError(null);
    }
  }, [isOpen, defaultBudgetPeriodId, availablePeriods]);

  if (!isOpen) {
    return null;
  }

  const parsedAmount = parseFloat(amount);
  const isValid =
    budgetPeriodId !== null &&
    description.trim().length > 0 &&
    amount.trim().length > 0 &&
    !Number.isNaN(parsedAmount) &&
    parsedAmount > 0;

  const handleSave = async () => {
    if (!isValid || budgetPeriodId === null) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        budget_period_id: budgetPeriodId,
        costpoint,
        type,
        amount: parsedAmount,
        description: description.trim(),
      });
      onClose();
    } catch {
      setError('Failed to save the commitment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center"
      data-testid="add-commitment-overlay"
    >
      <div
        className="relative top-4 sm:top-10 p-4 sm:p-5 border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto m-4 sm:m-0"
        role="dialog"
        data-testid="add-commitment-modal"
      >
        <h3 className="text-lg font-medium text-gray-900 mb-1">Add Commitment</h3>
        <p className="text-sm text-gray-500 mb-4">
          Something already agreed - an expense or income you know is coming - that hasn't
          shown up as a real transaction yet.
        </p>

        {availablePeriods.length === 0 && (
          <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-md">
            <p className="text-sm text-yellow-800">
              No budget period exists for {year} yet - create one on the Budgets page first.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {availablePeriods.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attach to budget period
              </label>
              <select
                value={budgetPeriodId ?? ''}
                onChange={(e) => setBudgetPeriodId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="commitment-budget-period-select"
              >
                {availablePeriods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {formatPeriodOption(period)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Costpoint</label>
            <div className="flex gap-2" data-testid="commitment-costpoint-select">
              {costpointOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex-1 text-center px-2 py-2 text-sm border rounded-md cursor-pointer ${
                    costpoint === option.value
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="commitment-costpoint"
                    value={option.value}
                    checked={costpoint === option.value}
                    onChange={() => setCostpoint(option.value)}
                    className="sr-only"
                  />
                  {getCostpointLabel(option.value)}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CommitmentType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="commitment-type-select"
            >
              <option value="expenses">Expense</option>
              <option value="income">Income</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this for?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="commitment-description-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (€)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="commitment-amount-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-commitment-submit-button"
          >
            {saving ? 'Saving...' : 'Add Commitment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCommitmentModal;
